import { useEffect, useState, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import styles from "./styles.module.css";
import { createMonitoringSession } from "../../../services/patient/details";
import { getPatientLocationId } from "../../../utils/helper";

const DailiesOORTracker = ({
  triggerSaveSignal,
  selectedTab = "RPM",
  patientId,
  locationId,
  flow,
  onTick,
}) => {
  const queryClient = useQueryClient();
  const [isTracking, setIsTracking] = useState(true);
  const [rpmTime, setRPMTime] = useState(0);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const previousPatientIdRef = useRef(patientId);

  // Ref mirrors rpmTime so event listeners/cleanup always read fresh value
  const rpmTimeRef = useRef(0);
  rpmTimeRef.current = rpmTime;

  // Ref to track isTracking for synchronous reads in start/pause functions
  const isTrackingRef = useRef(true);
  isTrackingRef.current = isTracking;

  // Flag to prevent auto-restart after manual save/pause
  const manualStopRef = useRef(false);

  // Store the elapsed time under THIS patient's own location, not the viewer's
  // current location (SCRUM-157). Held in a ref kept in lockstep with the
  // locationId prop so the auto-save (which fires on unmount, when the parent
  // may already be switching patients) uses the right patient's location.
  // Falls back to getPatientLocationId() (which reads the captured _pt_loc_id,
  // then current_loc) when no prop is supplied, so saves never break.
  const locationIdRef = useRef(locationId);
  useEffect(() => {
    if (locationId != null) locationIdRef.current = locationId;
  }, [locationId]);

  const mutation = useMutation(
    (sessionData) =>
      createMonitoringSession(
        sessionData,
        previousPatientIdRef.current,
        locationIdRef.current ?? getPatientLocationId()
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["care-manager-summary"]);
      },
      onError: () => {},
    }
  );

  // Match the patient-details header pill: minute-scale MM:SS with no
  // hours segment (90 minutes reads 90:00, not 01:30:00).
  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const startTracking = () => {
    // Clear manual stop flag when user explicitly clicks Play
    manualStopRef.current = false;

    // Clear any existing interval first to prevent duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsTracking(true);
    startTimeRef.current = new Date().toISOString();
    intervalRef.current = setInterval(() => {
      setRPMTime((prev) => prev + 1);
    }, 1000);
  };

  const pauseTracking = () => {
    if (isTrackingRef.current) {
      manualStopRef.current = true;
      setIsTracking(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Discard — stops the clock, resets to 0, does NOT save.
  // User must press Play manually to start again.
  const discardAndStop = () => {
    manualStopRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    setRPMTime(0);
    rpmTimeRef.current = 0;
    startTimeRef.current = null;
  };

  // Save — only called by the three legitimate triggers below
  const saveSession = useCallback(() => {
    const currentTime = rpmTimeRef.current;
    if (!startTimeRef.current || currentTime === 0) return;

    const payload = {
      monitoring_seconds: currentTime,
      total_rpm_seconds: currentTime,
      ...(flow === "Dailies"
        ? { dailies_flow: true }
        : { out_of_range_flow: true }),
    };
    mutation.mutate(payload);
  }, [flow]);

  // ── Start interval when component mounts / patientId changes ────────────
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset manual stop flag on patient change (fresh start)
    manualStopRef.current = false;

    setIsTracking(true);
    setRPMTime(0);
    startTimeRef.current = new Date().toISOString();
    intervalRef.current = setInterval(() => {
      setRPMTime((prev) => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [patientId]);

  // ── 2. Save on triggerSaveSignal (next/previous patient navigation OR manual save)
  //    Resets timer to 0 AND stops tracking — matches Patient Dashboard behavior
  useEffect(() => {
    if (triggerSaveSignal) {
      // Clear interval immediately
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      saveSession();
      setRPMTime(0);
      setIsTracking(false); // Stop tracking after manual save
      manualStopRef.current = true; // Mark as manually stopped
      previousPatientIdRef.current = patientId;
      startTimeRef.current = new Date().toISOString();
    }
  }, [triggerSaveSignal]);

  // ── 3. Save on component unmount (leaving the Dailies tab / page) ──────────
  useEffect(() => {
    return () => {
      saveSession();
    };
  }, []);

  // ── 4. Save on browser tab close / page refresh ────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveSession();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [saveSession]);

  // ── Notify parent of timer updates ───────────────────────────────────────────
  useEffect(() => {
    onTick?.({ elapsedTimeRPM: rpmTime, elapsedTimeCCM: 0 });
  }, [rpmTime]);

  return (
    <div className={styles.trackerWrapper}>

      {/* 1 — Pause / Play button (leftmost) — matches Figma order */}
      <button
        className={styles.trackerPauseBtn}
        onClick={() => (isTracking ? pauseTracking() : startTracking())}
        aria-label={isTracking ? "Pause" : "Resume"}
        title={isTracking ? "Pause" : "Resume"}
      >
        <FontAwesomeIcon icon={isTracking ? faPause : faPlay} />
      </button>

      {/* 2 — Gray pill: "03:37 RPM" (center) — matches Figma */}
      <div className={styles.trackerPill}>
        <span className={styles.trackerTime}>{formatTime(rpmTime)}</span>
        <span className={styles.trackerLabel}>RPM</span>
      </div>

      {/* 3 — Discard button — stops + resets timer WITHOUT saving.
              User must press Play to start tracking again. */}
      <button
        className={styles.trackerHistoryBtn}
        onClick={discardAndStop}
        aria-label="Discard and stop timer"
        title="Discard and stop timer"
      >
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </button>

    </div>
  );
};

export default DailiesOORTracker;